import React, { useEffect, useRef, useState } from "react";
import api from "@/api";
import { useAuth } from "@/contexts/AuthContext";
import {
  Plus, Trash2, Edit, Play, CheckCircle, XCircle, Webhook,
  Globe, Copy, Download, Upload, Users, Building2
} from "lucide-react";

const TRIGGER_LABELS = { extraction_complete: "On Extraction", approved: "On Approve", both: "Both" };
const METHOD_COLORS = { POST: "bg-blue-50 text-blue-700", GET: "bg-emerald-50 text-emerald-700", PUT: "bg-amber-50 text-amber-700" };

// ── Allocate Modal ─────────────────────────────────────────────────────────────

function AllocateModal({ webhook, companies, onClose, onSave }) {
  const [selected, setSelected] = useState(new Set(webhook.allocated_company_ids || []));
  const [saving, setSaving] = useState(false);

  const toggle = (id) => setSelected(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/webhooks/${webhook.id}/allocate`, { company_ids: [...selected] });
      onSave();
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl border border-slate-200 w-full max-w-sm shadow-xl" onClick={e => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-2">
          <Users size={14} className="text-[#006080]" />
          <div>
            <h3 className="font-bold text-slate-900 text-sm">Allocate "{webhook.name}"</h3>
            <p className="text-xs text-slate-400">Select which companies can use this global webhook</p>
          </div>
        </div>
        <div className="p-4 max-h-64 overflow-y-auto space-y-1">
          {companies.length === 0 && <p className="text-sm text-slate-400 text-center py-4">No companies found</p>}
          {companies.map(co => (
            <label key={co.id} className="flex items-center gap-2.5 p-2 rounded-md hover:bg-slate-50 cursor-pointer">
              <input type="checkbox" checked={selected.has(co.id)} onChange={() => toggle(co.id)}
                className="w-4 h-4 rounded border-slate-300 text-[#006080] focus:ring-[#006080]" />
              <span className="text-sm text-slate-700">{co.name}</span>
            </label>
          ))}
        </div>
        <div className="px-4 py-3 border-t border-slate-200 flex gap-2">
          <button onClick={onClose} className="flex-1 h-8 border border-slate-200 text-sm text-slate-600 rounded-md hover:bg-slate-50">Cancel</button>
          <button onClick={handleSave} disabled={saving} className="flex-1 h-8 bg-[#006080] text-white text-sm font-medium rounded-md disabled:opacity-50">
            {saving ? "Saving…" : "Save Allocation"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Import Modal ───────────────────────────────────────────────────────────────

function ImportModal({ onClose, onSave }) {
  const [json, setJson] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const fileRef = useRef();

  const handleFile = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = ev => setJson(ev.target.result);
    reader.readAsText(f);
  };

  const handleImport = async () => {
    setError("");
    let parsed;
    try { parsed = JSON.parse(json); } catch { setError("Invalid JSON"); return; }
    setSaving(true);
    try {
      await api.post("/webhooks/import", parsed);
      onSave();
    } catch (err) {
      setError(err.response?.data?.detail || "Import failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl border border-slate-200 w-full max-w-md shadow-xl" onClick={e => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-2">
          <Upload size={14} className="text-[#006080]" />
          <h3 className="font-bold text-slate-900 text-sm">Import Webhook</h3>
        </div>
        <div className="p-5 space-y-3">
          {error && <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</div>}
          <div className="flex items-center gap-2">
            <button onClick={() => fileRef.current?.click()} className="h-8 px-3 text-xs font-medium border border-slate-200 rounded-md hover:bg-slate-50 text-slate-600">Choose JSON file</button>
            <span className="text-xs text-slate-400">or paste JSON below</span>
            <input ref={fileRef} type="file" accept=".json" onChange={handleFile} className="hidden" />
          </div>
          <textarea value={json} onChange={e => setJson(e.target.value)} rows={7}
            placeholder='{"name": "My Webhook", "url": "https://...", ...}'
            className="w-full px-3 py-2 text-xs font-mono border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-[#006080] resize-none" />
        </div>
        <div className="px-5 pb-4 flex gap-2">
          <button onClick={onClose} className="flex-1 h-8 border border-slate-200 text-sm text-slate-600 rounded-md hover:bg-slate-50">Cancel</button>
          <button onClick={handleImport} disabled={saving || !json.trim()} className="flex-1 h-8 bg-[#006080] text-white text-sm font-medium rounded-md disabled:opacity-50">
            {saving ? "Importing…" : "Import Webhook"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Create/Edit Modal ──────────────────────────────────────────────────────────

function WebhookModal({ webhook, isSuperAdmin, onClose, onSave }) {
  const isEdit = !!webhook?.id;
  const [form, setForm] = useState(webhook || { name: "", url: "", http_method: "POST", trigger: "both", payload_template: "", is_active: true, headers: {}, scope: "company" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      if (isEdit) await api.put(`/webhooks/${webhook.id}`, form);
      else await api.post("/webhooks", form);
      onSave();
    } catch (err) {
      setError(err.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-lg border border-slate-200 w-full max-w-lg animate-fade-in overflow-y-auto max-h-[90vh]" onClick={e => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-slate-200 sticky top-0 bg-white">
          <h3 className="font-bold text-slate-900">{isEdit ? "Edit Webhook" : "New Webhook"}</h3>
        </div>
        <form onSubmit={handleSave} className="p-6 space-y-4">
          {error && <div className="p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">{error}</div>}
          <div>
            <label className="text-xs font-bold uppercase tracking-wide text-slate-500 block mb-1.5">Name</label>
            <input data-testid="webhook-name-input" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required className="w-full h-9 px-3 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-[#006080]" />
          </div>
          <div className="flex gap-3">
            <div className="flex-1">
              <label className="text-xs font-bold uppercase tracking-wide text-slate-500 block mb-1.5">URL</label>
              <input data-testid="webhook-url-input" type="url" value={form.url} onChange={e => setForm(f => ({ ...f, url: e.target.value }))} required placeholder="https://..." className="w-full h-9 px-3 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-[#006080]" />
            </div>
            <div className="w-28">
              <label className="text-xs font-bold uppercase tracking-wide text-slate-500 block mb-1.5">Method</label>
              <select data-testid="webhook-method-select" value={form.http_method} onChange={e => setForm(f => ({ ...f, http_method: e.target.value }))} className="w-full h-9 px-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-[#006080] bg-white">
                {["POST", "GET", "PUT", "PATCH"].map(m => <option key={m}>{m}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wide text-slate-500 block mb-1.5">Trigger</label>
            <select data-testid="webhook-trigger-select" value={form.trigger} onChange={e => setForm(f => ({ ...f, trigger: e.target.value }))} className="w-full h-9 px-3 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-[#006080] bg-white">
              <option value="extraction_complete">On Extraction Complete</option>
              <option value="approved">On Document Approved</option>
              <option value="both">Both</option>
            </select>
          </div>
          {isSuperAdmin && !isEdit && (
            <div>
              <label className="text-xs font-bold uppercase tracking-wide text-slate-500 block mb-1.5">Scope</label>
              <select value={form.scope} onChange={e => setForm(f => ({ ...f, scope: e.target.value }))} className="w-full h-9 px-3 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-[#006080] bg-white">
                <option value="company">Company</option>
                <option value="global">Global (template)</option>
              </select>
            </div>
          )}
          <div>
            <label className="text-xs font-bold uppercase tracking-wide text-slate-500 block mb-1">
              Payload Template <span className="text-slate-400 font-normal normal-case">(optional)</span>
            </label>
            <textarea data-testid="webhook-payload-input" value={form.payload_template || ""} onChange={e => setForm(f => ({ ...f, payload_template: e.target.value }))} rows={4}
              placeholder={'{"id": "${document_id}", "data": ${extracted_fields}}'}
              className="w-full px-3 py-2 text-xs font-mono border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-[#006080] resize-none" />
          </div>
          <div className="flex items-center gap-2">
            <input type="checkbox" id="wh_active" checked={form.is_active} onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))} className="accent-[#006080]" />
            <label htmlFor="wh_active" className="text-sm text-slate-700">Active</label>
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 h-9 border border-slate-200 text-sm font-medium text-slate-700 rounded-md hover:bg-slate-50">Cancel</button>
            <button data-testid="save-webhook-button" type="submit" disabled={saving} className="flex-1 h-9 bg-[#006080] text-white text-sm font-medium rounded-md hover:bg-[#004B66] disabled:opacity-50">
              {saving ? "Saving..." : isEdit ? "Save" : "Create"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────────

export default function Webhooks() {
  const [webhooks, setWebhooks] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [testing, setTesting] = useState(null);
  const [testResult, setTestResult] = useState({});
  const [showImport, setShowImport] = useState(false);
  const [allocateTarget, setAllocateTarget] = useState(null);
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "superadmin";

  const fetchWebhooks = () => {
    setLoading(true);
    const calls = [api.get("/webhooks")];
    if (isSuperAdmin) calls.push(api.get("/admin/companies"));
    Promise.all(calls)
      .then(([r, c]) => {
        setWebhooks(r.data);
        if (c) setCompanies(c.data.filter(co => co.is_active));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchWebhooks(); }, []);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this webhook?")) return;
    await api.delete(`/webhooks/${id}`);
    fetchWebhooks();
  };

  const handleTest = async (id) => {
    setTesting(id);
    try {
      const r = await api.post(`/webhooks/${id}/test`);
      setTestResult(prev => ({ ...prev, [id]: r.data }));
    } catch (e) {
      setTestResult(prev => ({ ...prev, [id]: { status: "failed", error_message: e.response?.data?.detail } }));
    } finally { setTesting(null); }
  };

  const handleDuplicate = async (wh) => {
    try {
      await api.post(`/webhooks/${wh.id}/duplicate`);
      fetchWebhooks();
    } catch {}
  };

  const handleExport = async (wh) => {
    try {
      const r = await api.get(`/webhooks/${wh.id}/export`);
      const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${wh.name.replace(/\s+/g, "_")}.webhook.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {}
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-900">Webhooks</h2>
          <p className="text-sm text-slate-500">Send document extraction data to external endpoints.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowImport(true)} data-testid="import-webhook-button"
            className="flex items-center gap-1.5 h-9 px-3 text-sm font-medium border border-slate-200 bg-white text-slate-600 rounded-md hover:bg-slate-50 transition-colors">
            <Upload size={13} /> Import
          </button>
          <button onClick={() => setModal({ type: "create" })} data-testid="create-webhook-button"
            className="flex items-center gap-2 h-9 px-4 bg-[#006080] hover:bg-[#004B66] text-white text-sm font-medium rounded-md transition-colors">
            <Plus size={14} /> New Webhook
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><div className="w-6 h-6 border-2 border-[#006080] border-t-transparent rounded-full animate-spin" /></div>
      ) : webhooks.length === 0 ? (
        <div className="text-center py-16 bg-white border border-slate-200 rounded-lg">
          <Webhook size={28} className="text-slate-300 mx-auto mb-3" />
          <p className="text-sm text-slate-500 mb-3">No webhooks configured</p>
          <button onClick={() => setModal({ type: "create" })} className="h-9 px-4 bg-[#006080] text-white text-sm font-medium rounded-md hover:bg-[#004B66] transition-colors">Create webhook</button>
        </div>
      ) : (
        <div className="space-y-3">
          {webhooks.map(wh => {
            const isGlobal = wh.scope === "global";
            return (
              <div key={wh.id} data-testid={`webhook-card-${wh.id}`} className="bg-white border border-slate-200 rounded-lg p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="font-semibold text-slate-900 text-sm">{wh.name}</span>
                      <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${METHOD_COLORS[wh.http_method] || "bg-slate-100 text-slate-600"}`}>{wh.http_method}</span>
                      <span className={`text-xs font-medium px-1.5 py-0.5 rounded-full ${wh.is_active ? "bg-[#E5F0F2] text-[#006080]" : "bg-slate-100 text-slate-500"}`}>
                        {wh.is_active ? "Active" : "Inactive"}
                      </span>
                      {isGlobal && (
                        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-purple-50 text-purple-600">Global</span>
                      )}
                    </div>
                    <p className="text-xs text-slate-500 font-mono truncate">{wh.url}</p>
                    <p className="text-xs text-slate-400 mt-1">Trigger: {TRIGGER_LABELS[wh.trigger]}</p>
                  </div>
                  <div className="flex items-center gap-1 flex-wrap">
                    <button onClick={() => handleTest(wh.id)} data-testid={`test-webhook-${wh.id}`} disabled={testing === wh.id}
                      className="flex items-center gap-1.5 h-7 px-2.5 text-xs font-medium border border-slate-200 rounded-md bg-white hover:bg-slate-50 transition-colors disabled:opacity-50">
                      {testing === wh.id ? <div className="w-3 h-3 border border-[#006080] border-t-transparent rounded-full animate-spin" /> : <Play size={11} />}
                      Test
                    </button>
                    <button onClick={() => handleDuplicate(wh)} data-testid={`duplicate-webhook-${wh.id}`} title="Duplicate"
                      className="flex items-center h-7 px-2 text-xs font-medium border border-slate-200 rounded-md bg-white hover:bg-slate-50 transition-colors text-slate-400">
                      <Copy size={11} />
                    </button>
                    <button onClick={() => handleExport(wh)} data-testid={`export-webhook-${wh.id}`} title="Export JSON"
                      className="flex items-center h-7 px-2 text-xs font-medium border border-slate-200 rounded-md bg-white hover:bg-slate-50 transition-colors text-slate-400">
                      <Download size={11} />
                    </button>
                    {isSuperAdmin && isGlobal && (
                      <button onClick={() => setAllocateTarget(wh)} data-testid={`allocate-webhook-${wh.id}`} title="Allocate to Companies"
                        className="flex items-center gap-1 h-7 px-2 text-xs font-medium border border-purple-200 rounded-md bg-purple-50 hover:bg-purple-100 text-purple-600 transition-colors">
                        <Users size={10} /> Allocate
                      </button>
                    )}
                    <button onClick={() => setModal({ type: "edit", webhook: wh })} data-testid={`edit-webhook-${wh.id}`}
                      className="flex items-center h-7 px-2 text-xs font-medium border border-slate-200 rounded-md bg-white hover:bg-slate-50 transition-colors text-slate-500">
                      <Edit size={11} />
                    </button>
                    <button onClick={() => handleDelete(wh.id)} data-testid={`delete-webhook-${wh.id}`}
                      className="flex items-center h-7 px-2 text-xs font-medium border border-slate-200 rounded-md bg-white hover:bg-red-50 hover:border-red-200 hover:text-red-600 transition-colors text-slate-400">
                      <Trash2 size={11} />
                    </button>
                  </div>
                </div>
                {testResult[wh.id] && (
                  <div data-testid={`test-result-${wh.id}`} className={`mt-3 p-2.5 rounded-md text-xs flex items-center gap-2 ${testResult[wh.id].status === "success" ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`}>
                    {testResult[wh.id].status === "success" ? <CheckCircle size={12} /> : <XCircle size={12} />}
                    {testResult[wh.id].status === "success" ? `Success — HTTP ${testResult[wh.id].response_code}` : testResult[wh.id].error_message || "Request failed"}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {modal?.type === "create" && <WebhookModal isSuperAdmin={isSuperAdmin} onClose={() => setModal(null)} onSave={() => { setModal(null); fetchWebhooks(); }} />}
      {modal?.type === "edit" && <WebhookModal webhook={modal.webhook} isSuperAdmin={isSuperAdmin} onClose={() => setModal(null)} onSave={() => { setModal(null); fetchWebhooks(); }} />}
      {showImport && <ImportModal onClose={() => setShowImport(false)} onSave={() => { setShowImport(false); fetchWebhooks(); }} />}
      {allocateTarget && (
        <AllocateModal
          webhook={allocateTarget}
          companies={companies}
          onClose={() => setAllocateTarget(null)}
          onSave={() => { setAllocateTarget(null); fetchWebhooks(); }}
        />
      )}
    </div>
  );
}
